import { InvoiceLine, ServiceItem, ServiceTransaction } from '../types';
// Blatt-Modul (Typen + flatRateLogic) — kein Zyklus.
import { hatBestandteile, istAbrechnungGesperrt, paketDeckel } from './bestandteile/bestandteilLogic';

/**
 * Rechenkern der Rueckschreibung: was ein Beleg im Projekt-LV tatsaechlich
 * bucht — und was seine Ruecknahme wieder herstellt.
 *
 * Herausgeloest aus invoiceService.writeBackToProject/revertWriteBack, wo
 * dieselbe Rechnung zwischen loadProjectById und saveProjectToCloud eingeklemmt
 * war und deshalb nur mit laufendem Supabase pruefbar. Genau hier liegen die
 * Invarianten, deren Bruch teuer ist: eine kumulative Rechnung, die den
 * Gesamtstand statt des Zuwachses bucht, macht offene Mengen unsichtbar; ein
 * Storno, das zu viel zurueckgibt, stellt sie ein zweites Mal in Rechnung.
 *
 * Pur: kein Supabase, kein Date.now(), kein crypto — Zeit und Ids werden
 * hereingereicht. Das folgt invoiceLogic.ts und ist im Projekt die Bedingung
 * dafuer, dass ueberhaupt getestet werden kann (es gibt kein jsdom).
 */

/**
 * Mengen unterhalb dieser Schwelle gelten als "nichts zu buchen".
 *
 * Mengen sind Gleitkommazahlen mit bis zu drei Nachkommastellen; 6 - 5.999
 * ergibt nicht exakt 0.001, und ein kumulativer Zuwachs von 6 - 6 kann als
 * 8.88e-16 herauskommen. Ein Vergleich auf === 0 liesse daraus einen
 * Logeintrag ueber praktisch nichts entstehen.
 */
export const BOOKING_EPSILON = 0.0005;

/** Zusammengefasste Rechnungszeilen EINER Leistung. */
export interface ServiceLineTotal {
    /** Summe der Zeilenmengen. Negativ bei Storno und Gutschrift. */
    qty: number;
    /** Summe der zugeordneten Protokollmengen ueber alle Zeilen. */
    allocated: number;
    hasAllocations: boolean;
    /**
     * Summe von previouslyBilledQty: was bei AUFBAU des Belegs schon abgerechnet
     * war. Nur kumulative Zeilen tragen den Wert; er ueberlebt das Spiegeln
     * (mirrorLines kopiert die Zeile per Spread) und sagt beim Storno, welcher
     * Teil der Zeilenmenge gar nicht von diesem Beleg stammt.
     */
    previouslyBilled: number;
    lvPosition: string;
}

/**
 * Fasst die Rechnungszeilen je Leistung zusammen.
 *
 * Diese Richtung ist tragend: NICHT ueber die Leistungen des Projekts
 * iterieren und die passende Zeile suchen. Die umgekehrte Richtung hatte zwei
 * stille Verluste: eine Zeile, deren serviceId im Projekt nicht (mehr)
 * vorkommt, fand keinen Partner und wurde uebergangen; und zwei Zeilen auf
 * dieselbe Leistung (Mengensplit) fielen auf die erste zusammen. In beiden
 * Faellen wurde der Kunde berechnet, waehrend die Position im Projekt offen
 * blieb — der naechste Rechnungslauf haette sie erneut abgerechnet.
 *
 * Zeilen mit quantityInformational tragen mit MENGE NULL bei: dort benennen
 * Menge und Preis nur die urspruengliche Position einer reinen
 * Betragskorrektur, abzurechnen ist an ihr nichts. Sie ganz zu ueberspringen
 * waere zu viel — die Leistung bliebe dann auch von assertServicesExist
 * ungeprueft, und eine zweite Zeile auf dieselbe Leistung (Mengensplit) muss
 * sich weiterhin mit ihr summieren lassen.
 */
export const aggregateLinesByService = (lines: InvoiceLine[]): Map<string, ServiceLineTotal> => {
    const byService = new Map<string, ServiceLineTotal>();
    for (const l of lines) {
        if (!l.serviceId) continue;
        const prev = byService.get(l.serviceId);
        byService.set(l.serviceId, {
            qty: (prev?.qty ?? 0) + (l.quantityInformational ? 0 : (l.quantity ?? 0)),
            allocated:
                (prev?.allocated ?? 0) +
                (l.allocations ?? []).reduce((sum, a) => sum + (a.quantity || 0), 0),
            hasAllocations: (prev?.hasAllocations ?? false) || !!l.allocations?.length,
            previouslyBilled: (prev?.previouslyBilled ?? 0) + (l.previouslyBilledQty ?? 0),
            lvPosition: prev?.lvPosition || l.lvPosition || '',
        });
    }
    return byService;
};

/**
 * Wirft, wenn eine Rechnungsposition auf eine Leistung zeigt, die es im
 * Projekt nicht (mehr) gibt.
 *
 * Ohne diese Schranke bliebe der Leistungsstand dieser Position unveraendert,
 * waehrend der Kunde sie bezahlt.
 */
export const assertServicesExist = (
    byService: ReadonlyMap<string, unknown>,
    services: ServiceItem[],
): void => {
    const knownServiceIds = new Set(services.map((s) => s.id));
    const orphaned = [...byService.keys()].filter((id) => !knownServiceIds.has(id));
    if (orphaned.length) {
        throw new Error(
            `${orphaned.length} Rechnungsposition(en) verweisen auf Leistungen, die es im Projekt nicht mehr gibt (${orphaned.join(', ')}). Der Leistungsstand liesse sich nicht fortschreiben; die Rechnung wird nicht ausgestellt.`,
        );
    }
};

/**
 * Was der stornierte Beleg je Leistung TATSAECHLICH gebucht hat.
 *
 * Einzige belastbare Quelle dafuer ist sein eigener Abdruck im
 * Leistungsprotokoll: planWriteBack hat dort je Leistung genau die gebuchte
 * Menge unter seiner Buchungsreferenz hinterlegt. Weder die Zeilenmenge noch
 * die Zuordnung sagen es zuverlaessig — die eine nennt bei kumulativer
 * Abrechnung den Gesamtstand, die andere fehlt oder deckt nur einen Teil ab.
 */
export const bookedByReference = (
    log: ServiceTransaction[],
    bookingReference: string,
): Map<string, number> => {
    const byService = new Map<string, number>();
    for (const t of log) {
        if (t.bookingReference !== bookingReference || !t.serviceId) continue;
        byService.set(t.serviceId, (byService.get(t.serviceId) ?? 0) + (t.quantity || 0));
    }
    return byService;
};

/**
 * Wie viel dieser Beleg fuer EINE Leistung tatsaechlich bucht — nicht dasselbe
 * wie seine Zeilenmenge.
 *
 * Kumulativ nennt die Zeile den GESAMTEN Leistungsstand: sechs Begehungen, von
 * denen frueher schon eine abgerechnet wurde. Gebucht wird der Zuwachs. Sonst
 * stiege der Abrechnungsstand um die alte Menge ein zweites Mal — und die
 * Ueberbuchungsschranke schlug schon vorher an ("abzurechnen 6, offen sind nur
 * 5"), sodass sich keine zweite kumulative Rechnung ausstellen liess. Eine
 * vollstaendig abgerechnete Position steht weiter auf dem Beleg, bucht aber
 * nichts mehr.
 *
 * Beim Storno zaehlt allein, was der Ursprungsbeleg gebucht hat
 * (originalBooked, aus seinem Abdruck im Protokoll). Die Zeilenmenge taugt dort
 * nicht: eine kumulative Rechnung ueber 20 hat bei 10 Vorabgerechneten nur 10
 * gebucht — ihr Storno haette ueber die Zeilenmenge 20 zurueckgenommen und die
 * 10 der Vorrechnung ein zweites Mal abrechenbar gemacht, ohne dass die
 * Unterbuchungsschranke anschlug (20 > 20 ist falsch).
 *
 * Ohne Abdruck bleiben zwei Rueckfaelle, in dieser Reihenfolge: die gespiegelte
 * Zuordnung, sonst die um previouslyBilled bereinigte Zeilenmenge. Der zweite
 * faengt genau die kumulativen Altbelege ab — bei ihnen ist die Differenz aus
 * Zeilenmenge und Vorabgerechnetem das, was gebucht wurde.
 */
export const bookedQuantity = (
    line: ServiceLineTotal,
    quantityBilled: number,
    cumulative: boolean,
    originalBooked?: number,
): number =>
    line.qty < 0
        ? originalBooked !== undefined
            ? -originalBooked
            : line.hasAllocations
              ? line.allocated
              : Math.min(0, line.qty + line.previouslyBilled)
        : cumulative
          ? Math.max(0, line.qty - quantityBilled)
          : line.qty;

export interface WriteBackOptions {
    /** true = die Zeilenmengen nennen den Gesamtstand, nicht den Zuwachs. */
    cumulative: boolean;
    bookingReference: string;
    /**
     * Buchungsreferenz des Belegs, den dieser hier aufhebt — beim Storno die des
     * Originals. Ist sie gesetzt, kommt die Ruecknahmemenge aus dessen Abdruck
     * im Protokoll (siehe bookedQuantity) statt aus den Zeilen.
     */
    reverseOf?: string;
    /** Leistungsprotokoll des Projekts. Nur fuer reverseOf gebraucht. */
    serviceLog?: ServiceTransaction[];
    /** Leistungsdatum des Logeintrags: Ende des Leistungszeitraums, sonst Belegdatum. */
    entryDate: string;
    /**
     * Belegnummer fuer den Beschreibungstext. FEHLT beim Ausstellen: die
     * Nummer wird erst gezogen, wenn alle Projekte geschrieben sind.
     *
     * Dann bleibt sie im Text weg. Frueher stand dort '(Entwurf)', und weil
     * der Protokolleintrag danach nie wieder angefasst wird, behauptete er
     * fuer immer, der Beleg sei einer. Der Bezug zum Beleg haengt an
     * bookingReference, nicht am Text — der Text soll nur nichts Falsches
     * sagen.
     */
    documentLabel?: string;
    actorId: string;
    actorName: string;
    /**
     * Auftraggeber, dem diese Buchung berechnet wird. Wird an jeden neuen
     * Protokolleintrag gestempelt.
     *
     * Ohne Aufteilung ist das schlicht der Kunde des Belegs — der Stempel
     * kostet dort nichts und macht den Bestand nachträglich zuordenbar, falls
     * das Projekt später geteilt wird.
     */
    customerId?: string;
    /**
     * Der TEILNEHMER der Aufteilung, dem diese Buchung berechnet wird
     * (project_splits.id). Feiner als `customerId`, siehe SQL Nr. 54.
     *
     * Gebraucht, sobald ein Auftraggeber mehrere Abschnitte hat: der Stand ist
     * dann je Abschnitt zu fuehren, und ohne den Stempel waere jede Buchung
     * eines solchen Projekts nicht zuordenbarer Altbestand — sie sperrte die
     * naechste Teilrechnung.
     */
    splitPartyId?: string;
    /** Anteil, mit dem gebucht wird (5000 = 50,00 %). Nur bei Aufteilung. */
    splitShareBp?: number;
    /**
     * Abrechnungsstand DIESES Auftraggebers je Leistung.
     *
     * Nur bei geteilten Projekten gesetzt. Kumulativ nennt die Zeile den
     * Gesamtstand des ANTEILS; wovon der Zuwachs abgezogen wird, ist dann
     * nicht mehr quantityBilled (die Summe über ALLE Auftraggeber), sondern
     * der Stand dieses einen. Andernfalls buchte der zweite Teilbeleg den
     * Zuwachs des ersten ein zweites Mal ab — oder gar nichts mehr.
     *
     * Fehlt die Karte, gilt unverändert quantityBilled. Der ungeteilte Beleg
     * läuft damit durch exakt denselben Code wie bisher.
     */
    billedForCustomer?: ReadonlyMap<string, number>;
    /** Hereingereicht statt crypto.randomUUID(), damit der Plan pruefbar bleibt. */
    newId: () => string;
    /** Hereingereicht statt new Date().toISOString(), aus demselben Grund. */
    bookedAt: () => string;
}

export interface WriteBackPlan {
    /** Fortgeschriebener Leistungsstand. Unberuehrte Leistungen sind identisch. */
    services: ServiceItem[];
    /** Neue Protokolleintraege, in der Reihenfolge der Leistungen. */
    logEntries: ServiceTransaction[];
}

/**
 * Berechnet den fortgeschriebenen Leistungsstand und die zugehoerigen
 * Protokolleintraege.
 *
 * Wirft bei Ueber- und Unterbuchung. Bewusst werfen statt still zu klemmen:
 * weniger abzurechnen, als das PDF ausweist, waere schlimmer als eine
 * fehlgeschlagene Ausstellung.
 */
export const planWriteBack = (
    services: ServiceItem[],
    lines: InvoiceLine[],
    opts: WriteBackOptions,
): WriteBackPlan => {
    const byService = aggregateLinesByService(lines);
    assertServicesExist(byService, services);

    // Der Abdruck des aufgehobenen Belegs im Protokoll. Fehlt reverseOf oder
    // steht dort nichts, faellt bookedQuantity auf Zuordnung bzw. Zeilenmenge
    // zurueck — Altbelege haben diesen Abdruck nicht immer.
    const originalBooked = opts.reverseOf
        ? bookedByReference(opts.serviceLog ?? [], opts.reverseOf)
        : undefined;

    const logEntries: ServiceTransaction[] = [];
    const nextServices = services.map((s) => {
        const line = byService.get(s.id);
        if (!line) return s;

        const billed = s.quantityBilled || 0;
        const used = s.quantityUsed || 0;
        // Der Stand, gegen den DIESER Beleg rechnet: bei Aufteilung der des
        // eigenen Auftraggebers, sonst der des Projekts.
        const billedBasis = opts.billedForCustomer
            ? (opts.billedForCustomer.get(s.id) ?? 0)
            : billed;
        const qty = bookedQuantity(line, billedBasis, opts.cumulative, originalBooked?.get(s.id));

        // Nichts zu buchen: die Position ist bereits vollstaendig abgerechnet
        // und steht nur als Teil des Gesamtstands auf dem Beleg. Ein Logeintrag
        // ueber null waere Rauschen in der Historie.
        if (Math.abs(qty) <= BOOKING_EPSILON) return s;

        if (qty >= 0) {
            // Normale Abrechnung: nie mehr buchen als offen ist.
            //
            // Mit derselben Toleranz wie der Storno-Zweig darunter, und aus
            // demselben Grund: die offene Menge entsteht als Differenz zweier
            // Gleitkommazahlen und traegt deren Staub. Eine auf dem
            // Tausendstel-Raster exakt geteilte Menge (1 Stück auf drei
            // Auftraggeber = 0,334 + 0,333 + 0,333) lässt beim letzten
            // Teilbeleg 1 - 0.667 = 0.33299999999999996 offen — 4e-17 zu wenig
            // für 0,333. Ohne Toleranz wäre der letzte Teilbeleg einer
            // Aufteilung nicht ausstellbar.
            //
            // Die Toleranz ist ein halbes Tausendstel und damit feiner als
            // jede Menge, die es auf einem Beleg gibt: eine echte Überbuchung
            // ist mindestens ein Tausendstel gross und schlägt weiter an.
            //
            // PAKETPOSITION (Kernregeln 2–4): die letzte Schranke vor dem
            // Schreiben. Ist das Gate nicht frei (Bestandteile offen ODER
            // Stand unbekannt — fail closed), ist nichts abrechenbar; sonst
            // hoechstens der Deckel (Vertragsmenge minus abgerechnet). Hier
            // stand frueher nur `used − billed`, und das kannte weder Gate
            // noch Deckel: eine von Hand auf den Beleg gesetzte gesperrte
            // Paketposition haette `quantityBilled` hochgezaehlt. Der Riegel
            // davor (componentLineIssues in planProjectIssue) meldet das schon
            // mit Klartext — diese Schranke haelt, falls ein Aufrufer ihn
            // umgeht. Nur fuer Abrechnungen; ein Storno muss immer gehen.
            const gesperrt = istAbrechnungGesperrt(s);
            const maxBillable = gesperrt
                ? 0
                : Math.max(0, Math.min(used - billed, paketDeckel(s)));
            if (qty > maxBillable + BOOKING_EPSILON) {
                throw new Error(
                    `Überbuchung bei "${s.title}": abzurechnen ${qty} ${s.unit}, offen sind nur ${maxBillable}` +
                        (gesperrt
                            ? ' — Paketposition, die Bestandteile sind noch nicht vollständig erledigt (erst abrechenbar, wenn alle erledigt sind).'
                            : hatBestandteile(s) && used - billed > maxBillable + BOOKING_EPSILON
                              ? ` — Paketposition, höchstens Vertragsmenge minus abgerechnet (${maxBillable}).`
                              : '.'),
                );
            }
        } else {
            // Storno: es kann nur zurueckgenommen werden, was abgerechnet
            // wurde. Sonst entstuende ein negativer Abrechnungsstand.
            //
            // Die Schranke ist die zweite Verteidigungslinie, nicht die erste:
            // sie vergleicht gegen den HEUTIGEN Stand und laesst deshalb genau
            // den Fall durch, der frueher zu viel zurueckgab (kumulativ 20
            // gebucht bei Stand 20, aber nur 10 davon aus diesem Beleg). Die
            // Menge selbst kommt jetzt aus dem Abdruck des Originals; hier
            // bleibt nur noch abzufangen, dass zwischen Buchung und Storno
            // anderweitig gemindert wurde. Mit Toleranz, weil Mengen
            // Gleitkommazahlen sind und 10.0000000001 > 10 kein Storno
            // verhindern darf.
            // Gegen den Stand des eigenen Auftraggebers: ein Teilbeleg kann nur
            // zurücknehmen, was SEIN Auftraggeber getragen hat. Gegen den
            // Gesamtstand geprüft ließe ein Storno der Teilrechnung A die
            // Menge von B mit abräumen — und B stünde danach mit einer
            // bezahlten, aber im Projekt wieder offenen Leistung da.
            if (-qty > billedBasis + BOOKING_EPSILON) {
                throw new Error(
                    `Storno bei "${s.title}": ${-qty} ${s.unit} sollen zurückgenommen werden, abgerechnet sind aber nur ${billedBasis}.`,
                );
            }
        }

        const newBilled = billed + qty;
        const isCancellation = qty < 0;
        logEntries.push({
            id: opts.newId(),
            date: opts.entryDate,
            serviceId: s.id,
            quantity: qty,
            performerId: opts.actorId,
            performerName: opts.actorName,
            isSubstitution: false,
            description: `${isCancellation ? 'Storno' : 'Rechnung'}${
                opts.documentLabel?.trim() ? ` ${opts.documentLabel.trim()}` : ''
            } — Pos. ${line.lvPosition || ''} (${qty} ${s.unit})`,
            bookedAt: opts.bookedAt(),
            bookedBy: opts.actorName,
            bookedById: opts.actorId,
            bookingReference: opts.bookingReference,
            customerId: opts.customerId,
            splitPartyId: opts.splitPartyId,
            splitShareBp: opts.splitShareBp,
        });

        return {
            ...s,
            quantityBilled: newBilled,
            // Der Status muss in BEIDE Richtungen folgen: nach einem Storno ist
            // die Position wieder offen, nicht dauerhaft "billed".
            status: newBilled >= used ? ('billed' as const) : ('done' as const),
        };
    });

    return { services: nextServices, logEntries };
};

/**
 * Idempotenz-Schicht 3: dieselbe Buchungsreferenz nie zweimal anlegen.
 *
 * Greift, wenn ein abgebrochener Ausstellversuch die Rueckschreibung schon
 * geschafft hatte. Ohne sie buchte der zweite Versuch dieselben Mengen ein
 * zweites Mal.
 */
export const isAlreadyBooked = (log: ServiceTransaction[], bookingReference: string): boolean =>
    log.some((t) => t.bookingReference === bookingReference);

export interface WriteBackRevertPlan {
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    /** Wie viele Protokolleintraege zurueckgenommen wurden. */
    revertedCount: number;
}

/**
 * Kehrt eine Rueckschreibung exakt um.
 *
 * null heisst "zu dieser Buchungsreferenz steht nichts im Protokoll" — dann
 * gibt es nichts zurueckzunehmen und das Projekt darf gar nicht erst
 * geschrieben werden.
 *
 * Die Eintraege werden tatsaechlich ENTFERNT, nicht storniert: sie gehoeren zu
 * einer Rechnung, die nie zustande kam. Es gibt keinen Beleg, auf den sie sich
 * beziehen.
 */
export const planWriteBackRevert = (
    services: ServiceItem[],
    log: ServiceTransaction[],
    bookingReference: string,
): WriteBackRevertPlan | null => {
    const mine = log.filter((t) => t.bookingReference === bookingReference);
    if (mine.length === 0) return null;

    const nextServices = services.map((s) => {
        const entry = mine.find((t) => t.serviceId === s.id);
        if (!entry) return s;
        const restored = Math.max(0, (s.quantityBilled || 0) - entry.quantity);
        return {
            ...s,
            quantityBilled: restored,
            status: restored < (s.quantityUsed || 0) ? ('done' as const) : s.status,
        };
    });

    return {
        services: nextServices,
        serviceLog: log.filter((t) => t.bookingReference !== bookingReference),
        revertedCount: mine.length,
    };
};
